import { useEffect, useRef } from "react";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import {
  peekResolvedPath,
  resolveRoutePath,
  RouteRenderMode,
  TimedPathPoint,
} from "../map-asset/multiDay/resolveRoutePath";

export type TripToResolve = {
  tripKey: string;
  tripIndex: number;
  points: VehicleMapDataPoint[];
};

// how many trips are road-aligned at once. each one is several Google calls
// made back to back, and DirectionsService starts returning
// OVER_QUERY_LIMIT if it's pushed much harder than this.
const RESOLVE_BATCH_SIZE = 6;

// Turns each trip's GPS fixes into the path that actually gets drawn, using
// whichever route view is active.
//
// Nothing here blocks the map and nothing here is surfaced: routes go up
// straight away on their raw fixes, and onResolved fires per trip so each
// one is swapped for its road-aligned path the moment it's ready. From the
// user's side the routes are simply drawn, then quietly settle onto the
// roads. Results are cached in resolveRoutePath, so switching views and
// back is free.
export const useResolvedRoutePaths = ({
  trips,
  mode,
  pathsRef,
  onResolved,
  onAllResolved,
}: {
  trips: TripToResolve[];
  mode: RouteRenderMode;
  // owned by the page so the renderer can read it at draw time - a ref, not
  // state, because a re-render per resolved trip would rebuild the map
  pathsRef: React.MutableRefObject<Map<string, TimedPathPoint[]>>;
  // `immediate` marks a route-view switch, which must land now rather
  // than queue behind a running animation
  onResolved: (
    tripKey: string,
    path: TimedPathPoint[],
    immediate?: boolean
  ) => void;
  onAllResolved?: () => void;
}) => {
  const runIdRef = useRef(0);

  // callbacks are read at call time so a new inline function each render
  // doesn't restart the work
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;
  const onAllResolvedRef = useRef(onAllResolved);
  onAllResolvedRef.current = onAllResolved;

  // identity-independent, so unrelated re-renders don't restart the work
  const tripsKey = trips
    .map((trip) => `${trip.tripKey}:${trip.points.length}`)
    .join(",");

  useEffect(() => {
    const runId = ++runIdRef.current;
    // the previous view's paths don't apply any more
    pathsRef.current = new Map();
    if (trips.length === 0) return;

    // Hand over anything already resolved for this view synchronously, in
    // the same tick as the switch. Until the geometry changes the route is
    // still drawn on the previous view's path while wearing this view's
    // styling, which reads as the line jumping somewhere the vehicle never
    // went - and awaiting a cached promise is still a tick too late.
    trips.forEach((trip) => {
      const cached = peekResolvedPath(mode, trip.tripKey, trip.points);
      if (!cached) return;
      pathsRef.current.set(trip.tripKey, cached);
      onResolvedRef.current(trip.tripKey, cached, true);
    });

    (async () => {
      for (let start = 0; start < trips.length; start += RESOLVE_BATCH_SIZE) {
        if (runId !== runIdRef.current) return;
        const batch = trips.slice(start, start + RESOLVE_BATCH_SIZE);
        await Promise.all(
          batch.map(async (trip) => {
            const path = await resolveRoutePath(
              mode,
              trip.tripKey,
              trip.tripIndex,
              trip.points
            );
            if (runId !== runIdRef.current) return;
            pathsRef.current.set(trip.tripKey, path);
            onResolvedRef.current(trip.tripKey, path);
          })
        );
        if (runId !== runIdRef.current) return;
      }
      if (runId !== runIdRef.current) return;
      onAllResolvedRef.current?.();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripsKey, mode]);
};
